import _ from "lodash";
import {SortOrder} from "antd/es/table/interface";
import {TablePaginationConfig} from "antd/lib/table";
import {useRef, useState} from "react";
import QueryOptions from "./QueryOptions";
import {loadLocalStorageValue, saveLocalStorageValue} from "./PersistentStorage";

interface PersistentData {
    pageSize: number;
}

export interface TableUiConfig {
    immediateMode: boolean;
    autoRefreshEnabled?: boolean;
    autoRefreshInterval?: number;
    visibleColumns: string[];
    showAiAnalytics?: boolean;
    showAiSummaryGroups?: boolean;
}

export interface TableHandler {
    queryOptions: QueryOptions;

    onTableChange: (pagination: any, filters: any, sorter: any) => void;

    onSearchSubmit: (filter: string, values?: any) => void;

    onRqbSearchSubmit: (rqbQuery: any) => void;

    columnSortOrder: (column: string) => SortOrder | undefined;

    pagination: TablePaginationConfig;

    updateTotal: (total: number) => void;

    resetPage: () => void;

    total: () => number;

    setLoading: (loading: boolean) => void;

    loading: boolean;

    reload: () => void;

    saveUiConfig: (config: TableUiConfig) => void;

    loadUiConfig: () => TableUiConfig | null;
}

export interface ColumnSortConfig {
    nulls?: Partial<Record<"asc" | "desc", "first" | "last">>;
}

export interface TableHandlerOptions {
    reloadFunction: () => Promise<void>;
    initialPageSize?: number;
    fixedFilter?: string;
    fixedRqbQuery?: any;
    persistentIdent?: string;
    sortConfig?: Record<string, ColumnSortConfig>;
}

export function useTableHandler(defaultSortOrder: string, options: TableHandlerOptions): TableHandler {
    const initialPageSize = (options.initialPageSize) ? options.initialPageSize : 25;
    const [pageSize] = useState(() => loadPageSize() ?? initialPageSize);

    const queryOptions = useRef(new QueryOptions(defaultSortOrder, pageSize, 0, options.fixedFilter, undefined, options.fixedRqbQuery));

    const [paginationConfig, setPaginationConfig] = useState<TablePaginationConfig>({
        pageSize,
        pageSizeOptions: ['10', '25', '50', '100', '250', '500', '1000'],
        placement: ['bottomRight', 'topRight'],
        showSizeChanger: true,
        hideOnSinglePage: false,
        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
        current: 1,
    });

    const [loading, setLoading] = useState<boolean>(false);

    function onTableChange(pagination: any, filters: any, sorter: any) {
        pagination.showTotal = (total, range) => `${range[0]}-${range[1]} of ${total} items`;

        if (sorter.field) {
            let key = (Array.isArray(sorter.field)) ? sorter.field.join(".") : sorter.field;

            if (sorter.columnKey) {
                key = sorter.columnKey;
            }

            queryOptions.current.orderBy = buildOrderBy(key, (sorter.order === 'descend') ? 'desc' : 'asc');
        } else {
            queryOptions.current.orderBy = defaultSortOrder;
        }

        queryOptions.current.top = pagination.pageSize;
        queryOptions.current.skip = ((pagination.current - 1) * pagination.pageSize);

        const pageSizeChanged = (paginationConfig.pageSize !== pagination.pageSize);

        if (pageSizeChanged) {
            pagination.current = 1;

            queryOptions.current.skip = 0;

            saveState(pagination);
        }

        setPaginationConfig(pagination);

        reload();
    }

    function buildOrderBy(key: string, direction: "asc" | "desc") {
        const nullsOrder = options.sortConfig?.[key]?.nulls?.[direction];
        const nullsClause = nullsOrder ? ` nulls ${nullsOrder}` : "";

        return `${key} ${direction}${nullsClause}`;
    }

    function onSearchSubmit(filter: string) {
        const effectiveFilter = buildEffectiveFilter(filter);
        const filterChanged = queryOptions.current.filter !== effectiveFilter;

        if (filterChanged) {
            queryOptions.current.filter = effectiveFilter;

            queryOptions.current.skip = 0;

            setPaginationConfig(prevState => Object.assign({}, prevState, {current: 1}));

            reload();
        }
    }

    function buildEffectiveFilter(filter: string) {
        if (options.fixedFilter && filter) {
            return `${options.fixedFilter} and ${filter}`;
        }

        return options.fixedFilter || filter;
    }

    function onRqbSearchSubmit(rqbQuery: any) {
        const effectiveRqbQuery = buildEffectiveRqbQuery(rqbQuery);
        const queryChanged = !_.isEqual(queryOptions.current.rqbQuery, effectiveRqbQuery);

        if (queryChanged) {
            queryOptions.current.rqbQuery = effectiveRqbQuery;

            queryOptions.current.skip = 0;

            setPaginationConfig(prevState => Object.assign({}, prevState, {current: 1}));

            reload();
        }
    }

    function buildEffectiveRqbQuery(rqbQuery: any) {
        if (options.fixedRqbQuery && rqbQuery) {
            return {combinator: "and", rules: [options.fixedRqbQuery, rqbQuery]};
        }

        return options.fixedRqbQuery || rqbQuery;
    }

    function resetPage() {
        queryOptions.current.skip = 0;

        setPaginationConfig(prevState => Object.assign({}, prevState, {current: 1}));
    }

    function reload() {
        setLoading(true);

        options.reloadFunction().finally(() => setLoading(false));
    }

    function saveState(pagination: TablePaginationConfig) {
        if (!options.persistentIdent) {
            return;
        }

        const data: PersistentData = {
            pageSize: pagination.pageSize || initialPageSize
        };

        saveLocalStorageValue(getQualifiedIdent(), JSON.stringify(data), getLegacyQualifiedIdent());
    }

    function loadPageSize() {
        if (!options.persistentIdent) {
            return undefined;
        }

        const serializedData = loadLocalStorageValue(getQualifiedIdent(), getLegacyQualifiedIdent());

        if (!serializedData) {
            return undefined;
        }

        const data: PersistentData = JSON.parse(serializedData);

        return data.pageSize;
    }

    function getQualifiedIdent() {
        return `Seal.Table.State.${options.persistentIdent}`;
    }

    function getLegacyQualifiedIdent() {
        return `Seal.Table.${options.persistentIdent}`;
    }

    function getStorageKeyForUiConfig() {
        return `Seal.Table.Ui.${options.persistentIdent}`;
    }

    function getLegacyStorageKeyForUiConfig() {
        return `Seal.Admin.Table.Ui.${options.persistentIdent}`;
    }

    function saveUiConfig(config: TableUiConfig) {
        saveLocalStorageValue(getStorageKeyForUiConfig(), JSON.stringify(config), getLegacyStorageKeyForUiConfig());
    }

    function loadUiConfig(): TableUiConfig | null {
        const serializedData = loadLocalStorageValue(getStorageKeyForUiConfig(), getLegacyStorageKeyForUiConfig());

        if (!serializedData) {
            return null;
        }

        return JSON.parse(serializedData);
    }

    function columnSortOrder(column: string): SortOrder | undefined {
        const [attribute, sortOrder] = queryOptions.current.orderBy.split(" ");

        if (column === attribute) {
            return sortOrder === "asc" ? 'ascend' : 'descend';
        }

        return undefined;
    }

    return {
        queryOptions: queryOptions.current,
        onTableChange: onTableChange,
        onSearchSubmit: onSearchSubmit,
        onRqbSearchSubmit: onRqbSearchSubmit,
        columnSortOrder: columnSortOrder,
        pagination: paginationConfig,
        updateTotal: (total: number) => setPaginationConfig(prevState => Object.assign({}, prevState, {total})),
        resetPage,
        total: () => paginationConfig.total,
        setLoading: (state: boolean) => setLoading(state),
        loading: loading,
        reload,
        saveUiConfig,
        loadUiConfig,
    }
}
